import { z } from 'zod';

import { HelpArticleStatusInput, HelpVisibilityInput } from '@nucleus-commerce/api-client/admin';

import { seoOverrideSchema } from '@/features/seo';

export const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
export const MAX_KEYWORDS = 30;
export const KEYWORD_MAX_LENGTH = 60;
export const MAX_POSITION = 10_000;

export const handleSchema = z
  .string()
  .trim()
  .min(1, { error: 'helpCenter:validation.handleRequired' })
  .max(120, { error: 'helpCenter:validation.handleLength' })
  .regex(HANDLE, { error: 'helpCenter:validation.handleFormat' });

/** Typed as text so an emptied input is not coerced to 0; "" lets the backend place it. */
export const positionSchema = z
  .string()
  .trim()
  .refine(
    (value) =>
      value === '' || (/^\d+$/.test(value) && Number(value) >= 0 && Number(value) <= MAX_POSITION),
    { error: 'helpCenter:validation.position' },
  );

/**
 * Keywords are free text (the questions shoppers ask in their own words), not slugs: only the
 * count and the length are limited.
 */
export const helpArticleFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { error: 'helpCenter:validation.titleRequired' })
    .max(200, { error: 'helpCenter:validation.titleLength' }),
  handle: handleSchema,
  excerpt: z.string().trim().max(500, { error: 'helpCenter:validation.excerptLength' }),
  bodyHtml: z.string().max(200_000, { error: 'helpCenter:validation.bodyLength' }),
  /** "" is no category. */
  categoryId: z.string(),
  keywords: z
    .array(z.string())
    .max(MAX_KEYWORDS, { error: 'helpCenter:validation.keywordsMax' })
    .refine(
      (keywords) =>
        keywords.every((k) => k.trim().length > 0 && k.trim().length <= KEYWORD_MAX_LENGTH),
      { error: 'helpCenter:validation.keywordLength' },
    ),
  /** "" shows the article in every locale. */
  locale: z.string(),
  position: positionSchema,
  status: z.enum(HelpArticleStatusInput),
  visibility: z.enum(HelpVisibilityInput),
  seo: seoOverrideSchema,
});

export type HelpArticleFormInput = z.input<typeof helpArticleFormSchema>;
export type HelpArticleFormOutput = z.output<typeof helpArticleFormSchema>;
